import { api } from '@/config/axios'
import type { PatientLoginResponse } from '@/types'

// API for patient authentication
export const patientLogin = async (email: string, password: string, code: string = ''): Promise<PatientLoginResponse> => {
    const response = await api.post('/auth/patient-login', {
        email,
        password,
        code,
    })
    return response.data
}

// Send patient code for SMS verification
export const sendPatientCode = async (email: string, password: string) => {
    const response = await api.post('/auth/patient-code', { email, password })
    return response.data
}

// Verify a two-factor token
export const twoFactorVerify = async (payload: { otp?: string; recovery_code?: string; remember_device?: boolean; device_name?: string }) => {
    const body: any = {}
    if (payload.otp) body.otp = payload.otp
    if (typeof payload.remember_device !== 'undefined') body.remember_device = payload.remember_device
    if (payload.device_name) body.device_name = payload.device_name

    const response = await api.post('/auth/2fa/verify', body)
    return response
}

// Enable 2FA (authenticated) - returns { qr_url, secret, recovery_codes }
export const enableTwoFactor = async () => {
    const response = await api.post('/auth/2fa/enable')
    return response.data
}

// Confirm 2FA during setup - body: { otp }
export const confirmTwoFactor = async (otp: string) => {
    const response = await api.post('/auth/2fa/confirm', { otp })
    return response.data
}

// Disable 2FA (authenticated)
export const disableTwoFactor = async () => {
    const response = await api.post('/auth/2fa/disable')
    return response.data
}

// Patient onboarding confirmation
export const patientConfirmation = async (token: string, email: string) => {
    const response = await api.post('/auth/patient-confirmation', { token, email })
    return response.data
}

// Verify patient date of birth
export const patientVerifyDob = async (token: string, email: string, dob: string) => {
    const response = await api.post('/auth/patient-verify-dob', { token, email, dob })
    return response.data
}

// Set patient password
export const patientSetPassword = async (token: string, email: string, password: string, password_confirmation: string) => {
    const response = await api.post('/auth/patient-set-password', { token, email, password, password_confirmation })
    return response.data
}

// Set patient phone number
export const patientSetPhoneNumber = async (token: string, email: string, phoneNumber: string) => {
    const response = await api.post('/auth/patient-set-phone-number', { token, email, phoneNumber })
    return response.data
}

// Send the security code
export const patientVerifySecurityCode = async (token: string, email: string, code: string) => {
    const response = await api.post('/auth/patient-verify-security-code', { token, email, code })
    return response.data
}

// Send market consent (authenticated via patient_token, set after security code verification)
export const patientVerifyMarketConsent = async (marketConsent: boolean) => {
    const response = await api.post('/auth/patient-verify-market-consent', { marketConsent })
    return response.data
}

// Request a password reset email
export const patientForgotPassword = async (email: string) => {
    const response = await api.post('/auth/patient-forgot-password', { email })
    return response.data
}

// Submit a new password using the reset token
export const patientResetPassword = async (token: string, email: string, password: string, password_confirmation: string) => {
    const response = await api.post('/auth/patient-reset-password', { token, email, password, password_confirmation })
    return response.data
}
